import { renderToString } from 'react-dom/server'
import { format, parseISO } from "date-fns"
import userCan from 'Shared/functions/userCan'

const renderStat = (value, label, percentage = true) =>
  renderToString(
    <>
      <span className="stat-percentage">
        {value.toFixed(0)}{percentage ? '%' : ''}
      </span>
      <br />
      <span className="stat-label">{label}</span>
    </>
  )

const columns = [
  {
    data: 'bookmarked',
    className: 'toggle-bookmark',
    width: '40px',
    orderable: false,
    render: (data, type, row) => {
      const isStarred = data === 'true'
      return renderToString(
        <button data-id={row.id} className={`toggle-bookmark button naked ${isStarred ? 'is-starred' : ''}`}>
          <span className='star-filled'>
            <SvgIconsBookmarkStar solid={isStarred} partial={true} />
          </span>
          <span className='star-empty'>
            <SvgIconsBookmarkStar solid={false} />
          </span>
          <span className='star-discard'>
            <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round'>
              <path d='M6 6l12 12M18 6L6 18' />
            </svg>
          </span>
        </button>
      )
    },
  },
  {
    data: 'name',
    className: 'bold verticalAlign',
    width: '35%',
    render: function (data, type, row, meta) {
      const tags = _.compact(decodeURIComponent(row['tags'] || '').split('||'))
      const date = row['date'] && row['status'] !== 'draft' ? format(parseISO(row['date'].replace(' ', 'T').replace(' UTC', 'Z')), "do MMMM yyyy, HH:mm") : ''
      const isDiscarded = row['discarded'] === 'true'
      const name = isDiscarded ? data : <a href={row['url']}>{data}</a>
      const prefix = row['primary_type'] === 'Phone' ? 'Launched at:' : 'Sent at:'
      const hasAbTest = _.isArray(row.stats) &&
        _.uniqBy(row.stats, 'content_variant_id').length > 1

      const showDate = row['date'] && row['status'] !== 'draft'
      const showTags = hasAbTest || tags.length > 0

      return renderToString(<div>
        {name}
        {showDate && <><br /><span className='small-text'>{prefix} {date}</span></>}
        {showTags && <><br />
          {hasAbTest && <span className='tag purple'>A/B test</span>}
          {tags.map(tag => <span className='tag' key={tag}>{tag}</span>)}
        </>}
        {isDiscarded && <><br />
          <button className="button secondary small margin-top half restore-campaign" data-campaign-id={row.DT_RowId}>Restore campaign</button>
        </>}
      </div>
      )
    },
  },
  {
    data: 'type',
    className: 'light',
    width: '26%',
    orderable: false,
    render: function (data, type, row, meta) {
      if (row['secondary_type']) {
        return renderToString(
          <>
            <span>{row['primary_type']}</span>
            <span className='ellipsis'></span>
            <span>{row['secondary_type']}</span>
          </>
        )
      } else {
        return renderToString(<span>{row['primary_type']}</span>)
      }
    },
  },
  {
    data: 'status',
    width: '8%',
    orderable: false,
    render: function (data, type, row, meta) {
      function getClass() {
        const status = row['status']
        const approvalStatus = row['approval_status']
        if (approvalStatus === 'awaiting_approval') return 'accent'
        if (approvalStatus === 'rejected') return 'red'
        if (status === 'live' || status === 'sent') return 'accent'
        if (status === 'closed') return 'blue-100'
        if (status === 'aborted') return 'red'

        return 'purple-60'
      }

      if (row['approval_status'] === 'awaiting_approval') {
        var status = 'Awaiting approval'
      } else if (row['approval_status'] === 'rejected') {
        var status = 'Approval rejected'
      } else {
        var status = row['status']
      }

      return renderToString(
        <span className={`bold ${getClass()} capitalize`}>{status}</span>
      )
    },
  },
  {
    data: 'recipients',
    className: 'light',
    width: '7%',
    orderable: false,
    render: function (data, type, row, meta) {
      const count = Number(row['recipients']).toLocaleString()
      return renderToString(
        <span className='dark-grey'>{count}</span>
      )
    }
  },
  {
    data: 'id',
    className: 'datatable-stat',
    width: '12%',
    orderable: false,
    render: (data, type, row) => {
      const canSum = _.isArray(row.stats)
      if (!canSum) {
        var statsRow = row.stats
      } else {
        var statsRow = row.stats.find(stats => !stats.send_phase) || row.stats[0]
      }

      if (row.status === 'draft') {
        return ''
      }
      let value = 0
      let label = ''
      let percentage = true
      if (row.type === 'sms_blast' || row.type === 'whatsapp_blast') {
        const clicks = canSum ? row.stats.reduce((acc, stats) => acc + stats.clicked_count, 0) : statsRow.clicked_count
        const messages = canSum ? row.stats.reduce((acc, stats) => acc + stats.text_messages_count, 0) : statsRow.text_messages_count
        value = clicks / messages * 100
        label = 'Clicks'
      }
      if (row.type === 'sms_conversations' || row.type === 'whatsapp_conversations') {
        value = canSum ? row.stats.reduce((acc, stats) => acc + stats.outstanding_conversations_count, 0) : statsRow.outstanding_conversations_count
        label = 'To reply to'
        percentage = false
      }
      if (row.type === 'sms_survey' || row.type === 'whatsapp_survey') {
        value = statsRow.surveys_count
        label = 'Surveys begun'
        percentage = false
      }
      if (row.type === 'phone') {
        value = statsRow.total_calls
        label = 'Calls made'
        percentage = false
      }
      if (row.type === 'email') {
        if (statsRow.emails_count === 0) {
          value = 0
        } else {
          value = (statsRow.opened_count / statsRow.emails_count) * 100
          label = 'Opened'
        }
      }
      if (isNaN(value)) {
        return '&nbsp;'
      }
      return renderStat(value, label, percentage)
    },
  },
  {
    data: 'id',
    className: 'datatable-stat',
    width: '12%',
    orderable: false,
    render: (data, type, row) => {
      if (!_.isArray(row.stats)) {
        var statsRow = row.stats
      } else {
        var statsRow = row.stats.reduce((acc, curr) => {
          Object.keys(curr).forEach(key => {
            if (typeof curr[key] === 'number') {
              acc[key] = (acc[key] || 0) + curr[key];
            } else if (Array.isArray(curr[key])) {
              acc[key] = [...(acc[key] || []), ...curr[key]];
            } else if (!(key in acc)) {
              acc[key] = curr[key];
            }
          });
          return acc;
        }, {});
      }

      if (row.status === 'draft') {
        return ''
      }
      let value = 0
      let label = ''
      let percentage = true
      // show reply rate for SMS blasts and conversations
      if (row.type === 'sms_blast' || row.type === 'sms_conversations' || row.type === 'whatsapp_conversations') {
        value = (statsRow.replied_count / statsRow.text_messages_count) * 100
        label = 'Replies'
      }
      // however for WhatsApp blasts, we have a read %
      if (row.type === 'whatsapp_blast') {
        value = (statsRow.read_count / statsRow.text_messages_count) * 100
        label = 'Read'
      }
      if (row.type === 'sms_survey' || row.type === 'whatsapp_survey') {
        value = statsRow.completed_count
        label = 'Surveys completed'
        percentage = false
      }
      if (row.type === 'phone') {
        value = (statsRow.pickup_rate || 0) * 100
        label = 'Pickup rate'
      }
      if (row.type === 'email') {
        if (statsRow.emails_count === 0) {
          value = 0
        } else {
          value = (statsRow.clicked_count / statsRow.emails_count) * 100
          label = 'Clicked'
        }
      }
      if (isNaN(value) || !isFinite(value)) {
        return '&nbsp;'
      }
      return renderStat(value, label, percentage)
    },
  },
  { data: 'created_at', visible: false },
  { data: 'launched_at', visible: false },
]

const headers = [
  '',
  'Name',
  'Campaign Type',
  'Status',
  'Recipients',
  '',
  '',
]

const organisationKinds = window.ENV.ORGANISATION_KINDS.map(kind => ({
  label: kind.label,
  value: kind.external_type
}))

const campaignViewingType = window.ENV.CAMPAIGN_VIEWING_TYPE

let filters = [
  {
    name: 'Type', column: 2, kind: 'radio', options: [
      { value: 'sms_blast', label: 'Text message blast' },
      { value: 'whatsapp_blast', label: 'WhatsApp blast' },
      { value: 'sms_survey', label: 'Text message survey' },
      { value: 'whatsapp_survey', label: 'WhatsApp survey' },
      { value: 'sms_conversations', label: 'Text message conversation' },
      { value: 'whatsapp_conversations', label: 'WhatsApp conversation' },
      { value: 'phone', label: 'Phone' },
      { value: 'email', label: 'Email' },
    ]
  },
  {
    name: 'Status', column: 3, kind: 'radio', options: [
      { value: 'draft', label: 'Draft' },
      { value: 'scheduled', label: 'Scheduled' },
      { value: 'live', label: 'Live' },
      { value: 'sent', label: 'Sent' },
      { value: 'paused', label: 'Paused' },
      { value: 'closed', label: 'Closed' },
      { value: 'aborted', label: 'Aborted' }
    ]
  },
  { name: 'Goal', kind: 'async', entity: 'effort' },
  { name: 'Tag', kind: 'async', entity: 'tag' },
]

if (userCan('manage_users')) {
  filters.push({ name: 'Created by', kind: 'async', entity: 'user' })
}

if (campaignViewingType !== 'automated_campaigns') {
  filters.push({
    name: 'Organisation', kind: 'async', entity: 'organisation'
  })
}

if (organisationKinds.length > 1 && campaignViewingType !== 'automated_campaigns') {
  filters.push({
    name: 'Organisation type', kind: 'radio-async', entity: 'external_type', options: organisationKinds
  })
}

filters.push({ name: 'Subscription topic', kind: 'subscription-topic', customParam: 'subscription_topic' })

const sorters = [
  { column: 7, label: 'Created', order: 'desc' },
  { column: 8, label: 'Launched', order: 'desc' },
  { column: 1, label: 'Alphabetically (A-Z)', order: 'asc' },
  { column: 1, label: 'Alphabetically (Z-A)', order: 'desc' }
]

const order = [[7, "desc"]]

const defaultFilters = [{
  name: "Organisation",
  label: window.ENV.ORGANISATION_NAME,
  value: window.ENV.ORGANISATION_ID
}]

export { columns, headers, filters, defaultFilters, sorters, order }
